/**
 * DeepfakeDetectionModal.js - Deepfake Detection Modal
 * Modal for uploading a video file OR pasting a URL to detect AI-generated / deepfake content.
 * Mirrors the pattern of VideoSummarizeModal.js exactly.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icons } from './Icons';
import { config } from '../config';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const isImageFile = (fileName) => {
  return config.deepfakeAllowedImageFormats.some(fmt => fileName.toLowerCase().endsWith(fmt));
};

const validateMediaFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected.' };
  }

  const fileName = file.name.toLowerCase();
  const allowedFormats = config.deepfakeAllowedFormats;
  const hasValidExtension = allowedFormats.some(fmt => fileName.endsWith(fmt));
  if (!hasValidExtension) {
    return {
      valid: false,
      error: `Unsupported format. Allowed: ${allowedFormats.join(', ')}`
    };
  }

  const isImage = isImageFile(fileName);
  const maxSizeMB = isImage ? config.deepfakeMaxImageFileSizeMB : config.deepfakeMaxFileSizeMB;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File too large (${formatFileSize(file.size)}). Maximum: ${maxSizeMB} MB.`
    };
  }

  return { valid: true, error: null };
};

const validateUrl = (url) => {
  if (!url || !url.trim()) {
    return { valid: false, error: 'Please enter a video URL.' };
  }
  try {
    const parsed = new URL(url.trim());
    if (!parsed.protocol.startsWith('http')) {
      return { valid: false, error: 'URL must start with http:// or https://' };
    }
  } catch {
    return { valid: false, error: 'Please enter a valid URL.' };
  }
  return { valid: true, error: null };
};

const TAB_UPLOAD = 'upload';
const TAB_URL = 'url';

function DeepfakeDetectionModal({ isOpen, onClose, onSubmitFile, onSubmitUrl, isLoading, initialValues }) {
  const [activeTab, setActiveTab] = useState(TAB_UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setVideoUrl('');
    setError('');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialValues || {};
    setError('');
    setIsDragging(false);
    if (initial.url) {
      setVideoUrl(initial.url);
      setSelectedFile(null);
      setActiveTab(TAB_URL);
    } else if (initial.initialFile) {
      setSelectedFile(initial.initialFile);
      setVideoUrl('');
      setActiveTab(TAB_UPLOAD);
    } else {
      setSelectedFile(null);
      setVideoUrl('');
      setActiveTab(TAB_UPLOAD);
    }
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const handleTabChange = (tab) => {
    if (isLoading) return;
    setActiveTab(tab);
    setError('');
  };

  const handleFileSelect = (file) => {
    setError('');
    const validation = validateMediaFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (activeTab === TAB_UPLOAD) {
      if (!selectedFile) {
        setError('Please select a file.');
        return;
      }
      const validation = validateMediaFile(selectedFile);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      onSubmitFile(selectedFile);
    } else {
      const validation = validateUrl(videoUrl);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      onSubmitUrl(videoUrl.trim());
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      resetState();
      onClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetState();
      onClose();
    }
  };

  const isSubmitDisabled = isLoading || (
    activeTab === TAB_UPLOAD ? !selectedFile : !videoUrl.trim()
  );

  return (
    <div className="video-modal-overlay" onClick={handleOverlayClick}>
      <div className="video-modal">
        <div className="video-modal-header">
          <div className="video-modal-title">
            <Icons.DeepfakeScan />
            <h2>Deepfake Detection</h2>
          </div>
          <button
            className="video-modal-close"
            onClick={handleClose}
            disabled={isLoading}
            type="button"
          >
            <Icons.Close />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="video-modal-content">
          {/* Tab Selector */}
          <div className="video-tabs">
            <button
              type="button"
              className={`video-tab ${activeTab === TAB_UPLOAD ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_UPLOAD)}
              disabled={isLoading}
            >
              Upload File
            </button>
            <button
              type="button"
              className={`video-tab ${activeTab === TAB_URL ? 'active' : ''}`}
              onClick={() => handleTabChange(TAB_URL)}
              disabled={isLoading}
            >
              URL
            </button>
          </div>

          {/* Upload File Tab */}
          {activeTab === TAB_UPLOAD && (
            <div className="video-upload-section">
              <p className="video-upload-hint">
                Video: {config.deepfakeAllowedVideoFormats.join(', ')} (max {config.deepfakeMaxFileSizeMB} MB) | Image: {config.deepfakeAllowedImageFormats.join(', ')} (max {config.deepfakeMaxImageFileSizeMB} MB)
              </p>

              {!selectedFile ? (
                <div
                  className={`video-drop-zone ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !isLoading && fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !isLoading && fileInputRef.current?.click()}
                >
                  <Icons.DeepfakeScan />
                  <span className="video-drop-zone-text">
                    {isDragging ? 'Drop file here' : 'Drag & drop a video or image file, or click to browse'}
                  </span>
                </div>
              ) : (
                <div className="video-file-info">
                  <div className="video-file-details">
                    <Icons.DeepfakeScan />
                    <div className="video-file-text">
                      <span className="video-file-name">{selectedFile.name}</span>
                      <span className="video-file-size">{formatFileSize(selectedFile.size)}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="video-file-remove"
                    onClick={handleRemoveFile}
                    disabled={isLoading}
                    title="Remove file"
                  >
                    <Icons.Close />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={config.deepfakeAllowedFormats.join(',')}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                disabled={isLoading}
              />
            </div>
          )}

          {/* Video URL Tab */}
          {activeTab === TAB_URL && (
            <div className="video-url-section">
              <p className="video-upload-hint">
                Video platforms: {config.deepfakeSupportedPlatforms} | Or paste a direct image URL
              </p>
              <input
                type="text"
                className="video-url-input"
                value={videoUrl}
                onChange={(e) => { setVideoUrl(e.target.value); setError(''); }}
                placeholder="https://example.com/video-or-image-url"
                disabled={isLoading}
                autoFocus
                autoComplete="off"
              />
            </div>
          )}

          {error && (
            <div className="video-error">
              {error}
            </div>
          )}

          <div className="video-modal-actions">
            <button
              type="button"
              className="video-btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="video-btn-submit"
              disabled={isSubmitDisabled}
            >
              {isLoading ? (
                <>
                  <span className="video-loading-spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <Icons.DeepfakeScan />
                  Analyze
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DeepfakeDetectionModal;
